import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileBarChart, Plus } from "lucide-react";
import { PageHeader } from "../../components/layout/PageHeader";
import { OverviewToolbar } from "../../components/layout/OverviewToolbar";
import { Modal } from "../../components/ui/Modal";
import { portalReportsAdminApi, type AdminReportListItem } from "../../api/portalReportsAdminApi";
import type { ReportAudience } from "../../api/portalReportsApi";

const AUDIENCE_LABEL: Record<ReportAudience, string> = { customer: "Customers", manager: "Managers", admin: "Admin" };

/**
 * Admin overview of dynamic reports (built on stored procedures): name, menu
 * label, audience, sections and assignment, with a "New report" action.
 */
export function ReportsAdminPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<AdminReportListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setItems(await portalReportsAdminApi.list());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load reports");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const all = items ?? [];
    if (!q) return all;
    return all.filter((r) => r.name.toLowerCase().includes(q) || r.menuLabel.toLowerCase().includes(q));
  }, [items, search]);

  return (
    <>
      <PageHeader
        title="Report builder"
        actions={
          <button type="button" className="btn btn-primary" onClick={() => setCreateOpen(true)} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Plus size={14} /> New report
          </button>
        }
      />
      <div className="dashboard">
        <OverviewToolbar search={search} onSearch={setSearch} placeholder="Search by name or menu label…" count={{ matched: filtered.length, total: items?.length ?? 0 }} />
        {error && <div className="tile" style={{ borderColor: "#e74c3c", color: "#c0392b", background: "#fdecea" }}>{error}</div>}
        {items == null && !error ? (
          <div className="tile" style={{ color: "var(--portal-muted)" }}>Loading reports…</div>
        ) : filtered.length === 0 ? (
          <div className="tile" style={{ color: "var(--portal-muted)" }}>
            No reports yet. Create one and add sections backed by <code>spPortalReport_*</code> procedures.
          </div>
        ) : (
          <div className="tile-rows">
            {filtered.map((r) => (
              <button
                key={r.portalReportId}
                type="button"
                className="tile tile-link tile-row"
                onClick={() => navigate(`/admin/reports/${encodeURIComponent(r.name)}`)}
                style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 16, padding: "12px 18px", textAlign: "left" }}
                aria-label={`Open ${r.name}`}
              >
                <span aria-hidden style={{ width: 40, height: 40, borderRadius: 10, background: "var(--portal-primary-soft)", display: "grid", placeItems: "center", flexShrink: 0 }}>
                  <FileBarChart size={18} style={{ color: "var(--portal-primary-darker)" }} />
                </span>
                <span style={{ display: "grid", gap: 2, minWidth: 0, flex: 1 }}>
                  <span style={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {r.name}
                    {!r.enabled && <span style={{ marginLeft: 8, fontSize: "0.75rem", fontWeight: 600, color: "#8a5a00", background: "#fff4e0", border: "1px solid #f0c36d", borderRadius: 999, padding: "1px 8px" }}>disabled</span>}
                  </span>
                  <span style={{ fontSize: "0.85rem", color: "var(--portal-muted)" }}>
                    Menu: {r.menuLabel} · {AUDIENCE_LABEL[r.audience] ?? r.audience} · {r.sectionCount} {r.sectionCount === 1 ? "section" : "sections"}
                    {r.audience === "customer" && <> · {r.assignedAll ? "all customers" : `${r.assignedCustomers} ${r.assignedCustomers === 1 ? "customer" : "customers"}`}</>}
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
      <CreateReportModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(name) => {
          setCreateOpen(false);
          navigate(`/admin/reports/${encodeURIComponent(name)}`);
        }}
      />
    </>
  );
}

function CreateReportModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (name: string) => void }) {
  const [name, setName] = useState("");
  const [menuLabel, setMenuLabel] = useState("");
  const [audience, setAudience] = useState<ReportAudience>("customer");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName("");
    setMenuLabel("");
    setAudience("customer");
    setBusy(false);
    setError(null);
  }, [open]);

  const submit = async () => {
    if (!name.trim()) return setError("Name is required.");
    setBusy(true);
    setError(null);
    try {
      const res = await portalReportsAdminApi.create({ name: name.trim(), menuLabel: menuLabel.trim() || name.trim(), audience });
      onCreated(res.name);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create the report");
    } finally {
      setBusy(false);
    }
  };

  const input: React.CSSProperties = { width: "100%", padding: "8px 10px", border: "2px solid var(--portal-border)", borderRadius: 8, fontSize: "0.9rem", fontFamily: "inherit", background: "white" };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New report"
      busy={busy}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={() => void submit()} disabled={busy}>{busy ? "Creating…" : "Create"}</button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 12 }}>
        {error && <div className="alert alert-error">{error}</div>}
        <label style={{ display: "grid", gap: 4, fontSize: "0.85rem" }}>
          <span style={{ color: "var(--portal-muted)" }}>Name (also the link, e.g. ProviderStats)</span>
          <input className="portal-input-inline" style={input} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </label>
        <label style={{ display: "grid", gap: 4, fontSize: "0.85rem" }}>
          <span style={{ color: "var(--portal-muted)" }}>Menu label</span>
          <input className="portal-input-inline" style={input} value={menuLabel} onChange={(e) => setMenuLabel(e.target.value)} placeholder={name || "Shown under Reports"} maxLength={50} />
        </label>
        <label style={{ display: "grid", gap: 4, fontSize: "0.85rem" }}>
          <span style={{ color: "var(--portal-muted)" }}>Audience</span>
          <select className="portal-input-inline" style={input} value={audience} onChange={(e) => setAudience(e.target.value as ReportAudience)}>
            <option value="customer">Customers (assigned per customer or all)</option>
            <option value="manager">Managers (scoped by @managerId)</option>
            <option value="admin">Admin only</option>
          </select>
        </label>
      </div>
    </Modal>
  );
}
